import type { CSSProperties } from 'react';

import classNames from 'classnames';

import BlueskyIcon from './components/BlueskyIcon';
import DiscordIcon from './components/DiscordIcon';
import FacebookIcon from './components/FacebookIcon';
import InstagramIcon from './components/InstagramIcon';
import LinkedinIcon from './components/LinkedInIcon';
import ThreadsIcon from './components/ThreadsIcon';
import TiktokIcon from './components/TiktokIcon';
import TwitterIcon from './components/TwitterIcon';
import YoutubeIcon from './components/YoutubeIcon';

import { computeAlignment } from '../utils/computeAlignment';
import { prefixUrlWithProtocol } from '../utils/prefixUrlWithProtocol';

import * as styles from './Socials.css';
import type { SocialsElementProps } from './Socials.types';

type SocialIconProps = {
  url: string;
  iconSize?: string;
  className?: string;
  style?: CSSProperties;
  Icon: React.ElementType;
  fill?: string;
};

const IconWrapper = ({ url, className, style, Icon, fill }: SocialIconProps) => {
  return (
    <a
      href={prefixUrlWithProtocol(url)}
      target="_blank"
      className={classNames(className, 'p-[--icon-padding] flex items-center justify-center')}
      style={{ ...style }}
      rel="noreferrer"
    >
      <Icon fill={fill} className={styles.socialIcon} />
    </a>
  );
};

export function SocialsElement(props: SocialsElementProps) {
  const { element, children, attributes, className, isLive } = props;
  const {
    data,
    padding = '10px',
    gap = '16px',
    iconSize = '14px',
    iconPadding = '4px',
    width = '100%',
    maxWidth = '100%',
    alignment,
    mobileAlignment,
    mobileGap,

    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    // Social media specific styles
    linkedinFill,
    linkedinBackground,
    linkedinBorderWidth,
    linkedinBorderColor,
    linkedinBorderRadius,
    linkedinBorderStyle,

    twitterFill,
    twitterBackground,
    twitterBorderWidth,
    twitterBorderColor,
    twitterBorderRadius,
    twitterBorderStyle,

    facebookFill,
    facebookBackground,
    facebookBorderWidth,
    facebookBorderColor,
    facebookBorderRadius,
    facebookBorderStyle,

    instagramFill,
    instagramBackground,
    instagramBorderWidth,
    instagramBorderColor,
    instagramBorderRadius,
    instagramBorderStyle,

    tiktokFill,
    tiktokBackground,
    tiktokBorderWidth,
    tiktokBorderColor,
    tiktokBorderRadius,
    tiktokBorderStyle,

    youtubeFill,
    youtubeBackground,
    youtubeBorderWidth,
    youtubeBorderColor,
    youtubeBorderRadius,
    youtubeBorderStyle,

    discordFill,
    discordBackground,
    discordBorderWidth,
    discordBorderColor,
    discordBorderRadius,
    discordBorderStyle,

    threadsFill,
    threadsBackground,
    threadsBorderWidth,
    threadsBorderColor,
    threadsBorderRadius,
    threadsBorderStyle,

    blueskyFill,
    blueskyBackground,
    blueskyBorderWidth,
    blueskyBorderColor,
    blueskyBorderRadius,
    blueskyBorderStyle,
  } = element.attrs;
  const hasSocials = Object.keys(data ?? {}).length > 0;

  if (!hasSocials) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.socialContainer,
        className,
        'flex flex-wrap items-center w-full relative',
        !isLive ? 'pointer-events-none' : '',
      )}
      style={
        {
          padding: padding,
          width: width,
          maxWidth: maxWidth,

          '--border-width': borderWidth,
          '--border-color': borderColor,
          '--border-style': borderStyle,
          '--border-radius': borderRadius,
          '--justify-content': computeAlignment(alignment),
          '--mobile-justify-content': computeAlignment(mobileAlignment || alignment),
          '--gap': gap,
          '--mobile-gap': mobileGap,

          '--icon-size': iconSize,
          '--icon-padding': iconPadding,

          '--linkedin-fill': linkedinFill,
          '--linkedin-background': linkedinBackground,
          '--linkedin-border-width': linkedinBorderWidth,
          '--linkedin-border-color': linkedinBorderColor,
          '--linkedin-border-radius': linkedinBorderRadius,
          '--linkedin-border-style': linkedinBorderStyle,

          '--twitter-fill': twitterFill,
          '--twitter-background': twitterBackground,
          '--twitter-border-width': twitterBorderWidth,
          '--twitter-border-color': twitterBorderColor,
          '--twitter-border-radius': twitterBorderRadius,
          '--twitter-border-style': twitterBorderStyle,

          '--facebook-fill': facebookFill,
          '--facebook-background': facebookBackground,
          '--facebook-border-width': facebookBorderWidth,
          '--facebook-border-color': facebookBorderColor,
          '--facebook-border-radius': facebookBorderRadius,
          '--facebook-border-style': facebookBorderStyle,

          '--instagram-fill': instagramFill,
          '--instagram-background': instagramBackground,
          '--instagram-border-width': instagramBorderWidth,
          '--instagram-border-color': instagramBorderColor,
          '--instagram-border-radius': instagramBorderRadius,
          '--instagram-border-style': instagramBorderStyle,

          '--tiktok-fill': tiktokFill,
          '--tiktok-background': tiktokBackground,
          '--tiktok-border-width': tiktokBorderWidth,
          '--tiktok-border-color': tiktokBorderColor,
          '--tiktok-border-radius': tiktokBorderRadius,
          '--tiktok-border-style': tiktokBorderStyle,

          '--youtube-fill': youtubeFill,
          '--youtube-background': youtubeBackground,
          '--youtube-border-width': youtubeBorderWidth,
          '--youtube-border-color': youtubeBorderColor,
          '--youtube-border-radius': youtubeBorderRadius,
          '--youtube-border-style': youtubeBorderStyle,

          '--discord-fill': discordFill,
          '--discord-background': discordBackground,
          '--discord-border-width': discordBorderWidth,
          '--discord-border-color': discordBorderColor,
          '--discord-border-radius': discordBorderRadius,
          '--discord-border-style': discordBorderStyle,

          '--threads-fill': threadsFill,
          '--threads-background': threadsBackground,
          '--threads-border-width': threadsBorderWidth,
          '--threads-border-color': threadsBorderColor,
          '--threads-border-radius': threadsBorderRadius,
          '--threads-border-style': threadsBorderStyle,

          '--bluesky-fill': blueskyFill,
          '--bluesky-background': blueskyBackground,
          '--bluesky-border-width': blueskyBorderWidth,
          '--bluesky-border-color': blueskyBorderColor,
          '--bluesky-border-radius': blueskyBorderRadius,
        } as CSSProperties
      }
      {...attributes}
    >
      {data?.twitter_url && (
        <IconWrapper
          url={data.twitter_url}
          className="bg-[--twitter-background]"
          style={{
            borderColor: twitterBorderColor,
            borderWidth: twitterBorderWidth,
            borderRadius: twitterBorderRadius,
            borderStyle: twitterBorderStyle,
          }}
          Icon={TwitterIcon}
          fill={twitterFill}
        />
      )}
      {data?.linkedin_url && (
        <IconWrapper
          url={data.linkedin_url}
          className="bg-[--linkedin-background]"
          style={{
            borderColor: linkedinBorderColor,
            borderWidth: linkedinBorderWidth,
            borderRadius: linkedinBorderRadius,
            borderStyle: linkedinBorderStyle,
          }}
          Icon={LinkedinIcon}
          fill={linkedinFill}
        />
      )}
      {data?.facebook_url && (
        <IconWrapper
          url={data.facebook_url}
          className="bg-[--facebook-background]"
          style={{
            borderColor: facebookBorderColor,
            borderWidth: facebookBorderWidth,
            borderRadius: facebookBorderRadius,
            borderStyle: facebookBorderStyle,
          }}
          Icon={FacebookIcon}
          fill={facebookFill}
        />
      )}
      {data?.instagram_url && (
        <IconWrapper
          url={data.instagram_url}
          className="bg-[--instagram-background]"
          style={{
            borderColor: instagramBorderColor,
            borderWidth: instagramBorderWidth,
            borderRadius: instagramBorderRadius,
            borderStyle: instagramBorderStyle,
          }}
          Icon={InstagramIcon}
          fill={instagramFill}
        />
      )}
      {data?.tiktok_url && (
        <IconWrapper
          url={data.tiktok_url}
          className="bg-[--tiktok-background]"
          style={{
            borderColor: tiktokBorderColor,
            borderWidth: tiktokBorderWidth,
            borderRadius: tiktokBorderRadius,
            borderStyle: tiktokBorderStyle,
          }}
          Icon={TiktokIcon}
          fill={tiktokFill}
        />
      )}
      {data?.youtube_url && (
        <IconWrapper
          url={data.youtube_url}
          className="bg-[--youtube-background]"
          style={{
            borderColor: youtubeBorderColor,
            borderWidth: youtubeBorderWidth,
            borderRadius: youtubeBorderRadius,
            borderStyle: youtubeBorderStyle,
          }}
          Icon={YoutubeIcon}
          fill={youtubeFill}
        />
      )}
      {data?.discord_url && (
        <IconWrapper
          url={data.discord_url}
          className="bg-[--discord-background]"
          style={{
            borderColor: discordBorderColor,
            borderWidth: discordBorderWidth,
            borderRadius: discordBorderRadius,
            borderStyle: discordBorderStyle,
          }}
          Icon={DiscordIcon}
          fill={discordFill}
        />
      )}
      {data?.threads_url && (
        <IconWrapper
          url={data.threads_url}
          className="bg-[--threads-background]"
          style={{
            borderColor: threadsBorderColor,
            borderWidth: threadsBorderWidth,
            borderRadius: threadsBorderRadius,
            borderStyle: threadsBorderStyle,
          }}
          Icon={ThreadsIcon}
          fill={threadsFill}
        />
      )}
      {data?.bluesky_url && (
        <IconWrapper
          url={data.bluesky_url}
          className="bg-[--bluesky-background]"
          style={{
            borderColor: blueskyBorderColor,
            borderWidth: blueskyBorderWidth,
            borderRadius: blueskyBorderRadius,
            borderStyle: blueskyBorderStyle,
          }}
          Icon={BlueskyIcon}
          fill={blueskyFill}
        />
      )}
      {children}
    </div>
  );
}
